import { createTheme, alpha, darken, lighten, getContrastRatio } from "@mui/material/styles";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { selectCurrentColor, selectColorMode } from "./themeSlice";

export const LIGHT_HEADER_BG = "#f1f1f1";
export const DARK_HEADER_BG = "#1e1e1e";
const MIN_CONTRAST = 3;

function ensureContrast(color, bg) {
  const bgIsDark = getContrastRatio("#fff", bg) > getContrastRatio("#000", bg);
  let safe = color;
  let i = 0;
  while (getContrastRatio(safe, bg) < MIN_CONTRAST && i < 20) {
    safe = bgIsDark ? lighten(safe, 0.1) : darken(safe, 0.1);
    i++;
  }
  return safe;
}

const useTheme = () => {
  const currentColor = useSelector(selectCurrentColor);
  const colorMode = useSelector(selectColorMode);

  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
  );

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    if (!mq?.addEventListener) return;
    const handler = (e) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const mode =
    colorMode === "system"
      ? systemDark ? "dark" : "light"
      : colorMode ?? "light";

  useEffect(() => {
    document.documentElement.setAttribute("data-color-mode", mode);
  }, [mode]);

  const color = currentColor || "#2371d1";
  const headerBg = mode === "dark" ? DARK_HEADER_BG : LIGHT_HEADER_BG;
  const safeOnLight = ensureContrast(color, headerBg);

  return createTheme({
    palette: {
      mode,
      primary: {
        main: color,
        transparent: alpha(color, 0.08),
        safeOnLight,
      },
    },
    typography: {
      fontFamily: ["Nunito", "sans-serif"].join(","),
      formHeading: {
        color: mode === "dark" ? "#e0e0e0" : "#333333",
        fontWeight: 300,
        fontSize: "18px",
        lineHeight: "24px",
        display: "block",
        textTransform: "capitalize",
      },
      formSubHeading: {
        fontSize: "12px",
        marginBottom: "5px",
        textTransform: "uppercase",
        fontWeight: 400,
        color: mode === "dark" ? "#aaaaaa" : "#9A9A9A",
        display: "block",
      },
    },
  });
};

export default useTheme;
